import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import Navbar from './Navbar.jsx';
import Footer from './Footer.jsx';
import { useTheme } from './ThemeProvider.jsx';

const Layout = ({ title, description, image, children, minimalHeader = false, hideFooter = false }) => {
  const location = useLocation();
  const { theme } = useTheme();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.pathname]);

  const currentUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${location.pathname}`
      : '';

  const metaTitle = title || 'NeoLabs | Modern App Development Company';
  const metaDescription =
    description ||
    'NeoLabs helps businesses reduce manual work, connect existing tools, and gain clearer operational visibility with personalized modern apps.';
  const metaImage = image || undefined;
  const themeColor = theme === 'dark' ? '#0A1018' : '#FAF7F2';

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'NeoLabs',
    url: currentUrl || 'https://neoredlabs.com',
    logo: '/favicon.png',
    description: metaDescription,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Makati City',
      addressRegion: 'Metro Manila',
      addressCountry: 'PH'
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        email: 'info@neolabs.com',
        contactType: 'sales'
      }
    ]
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'NeoLabs',
    url: currentUrl || 'https://neoredlabs.com'
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        {metaDescription && <meta name="description" content={metaDescription} />}
        <meta name="robots" content="index,follow" />
        <meta name="theme-color" content={themeColor} />
        <meta name="color-scheme" content={theme === 'dark' ? 'dark light' : 'light dark'} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="NeoLabs" />
        {metaImage && <meta property="og:image" content={metaImage} />}
        {currentUrl && <meta property="og:url" content={currentUrl} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        {metaImage && <meta name="twitter:image" content={metaImage} />}
        {currentUrl && <link rel="canonical" href={currentUrl} />}
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-transparent text-ink">
        <Navbar minimal={minimalHeader} />
        <div className={minimalHeader ? 'h-20 shrink-0 sm:h-24' : 'h-24 shrink-0 sm:h-28'} aria-hidden />
        <main>{children}</main>
        {!hideFooter && <Footer />}
      </div>
    </>
  );
};

export default Layout;
